import { useEffect, useMemo, useState } from 'react';
import { Link, useSearch } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import L from 'leaflet';
import {
  CircleMarker,
  MapContainer,
  Popup,
  TileLayer,
  useMap,
} from 'react-leaflet';
import type { PortalCockpitStorePin } from '@shared/portal-api-types';
import type { TrackerOperationalStatus } from '@shared/tracker-types';
import {
  fetchPortalCockpit,
  fetchPortalContext,
  fetchPortalCycles,
} from '@/lib/portalApi';
import { PortalShell } from './PortalShell';
import { PortalCyclePickerFields, PortalErrorBlock, PortalLoadingBlock } from './portalUi';
import { buildPortalSectionHref } from './portalCycleQuery';
import { usePortalCycleSelection } from './usePortalCycleSelection';
import { TrackerMapLegend } from '@/pages/tracker/TrackerMapLegend';
import {
  getOperationalPinColor,
  getOperationalPinStroke,
  OperationalStatusBadge,
  RejectionIndicator,
} from '@/pages/tracker/operationalStatusUi';

const DEFAULT_MAP_CENTER: [number, number] = [53.3498, -6.2603];
const POLL_INTERVAL_MS = 30000;

function FitBounds({ points }: { points: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [map, points]);
  return null;
}

function hasValidCoords(store: PortalCockpitStorePin): boolean {
  return store.lat != null && store.lng != null && !Number.isNaN(store.lat) && !Number.isNaN(store.lng);
}

function toTrackerStatus(status: PortalCockpitStorePin['operationalStatus']): TrackerOperationalStatus {
  return status;
}

function queryErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export function PortalCockpitPage() {
  const search = useSearch();
  const { data: context, isLoading: contextLoading, isError: contextError, error: contextErrorDetail } = useQuery({
    queryKey: ['portal', 'context'],
    queryFn: fetchPortalContext,
  });

  const {
    data: cyclesData,
    isLoading: cyclesLoading,
    isError: cyclesError,
    error: cyclesErrorDetail,
  } = useQuery({
    queryKey: ['portal', 'cycles'],
    queryFn: fetchPortalCycles,
    enabled: !!context?.features.cockpit,
  });

  const { selectedCycleId, onCycleChange } = usePortalCycleSelection(cyclesData?.items);
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);

  useEffect(() => {
    setSelectedStoreId(null);
  }, [selectedCycleId]);

  const {
    data: cockpit,
    isLoading: cockpitLoading,
    isError: cockpitError,
    error: cockpitErrorDetail,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['portal', 'cockpit', selectedCycleId],
    queryFn: () => fetchPortalCockpit(selectedCycleId!),
    enabled: selectedCycleId != null,
    refetchInterval: POLL_INTERVAL_MS,
  });

  const mapPoints = useMemo(
    () => (cockpit?.stores ?? []).filter(hasValidCoords).map((store) => [store.lat!, store.lng!] as [number, number]),
    [cockpit?.stores],
  );

  const selectedStore = cockpit?.stores.find((store) => store.storeId === selectedStoreId) ?? null;

  if (contextLoading) return <PortalLoadingBlock />;
  if (contextError || !context) {
    return <PortalErrorBlock message={queryErrorMessage(contextErrorDetail, 'Could not load portal context.')} />;
  }

  if (!context.features.cockpit) {
    return (
      <PortalShell context={context}>
        <PortalErrorBlock message="Cockpit is not enabled for your account." />
      </PortalShell>
    );
  }

  return (
    <PortalShell context={context}>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Activation cockpit</h2>
          <p className="text-sm text-muted-foreground">
            Cycle progress and store status for {cockpit?.retailerName ?? context.allowedRetailers[0]?.name}.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <PortalCyclePickerFields
            cycles={cyclesData?.items ?? []}
            selectedCycleId={selectedCycleId}
            onCycleChange={onCycleChange}
            externalSelection
            disabled={cyclesLoading || cyclesError}
            errorMessage={cyclesError
              ? queryErrorMessage(cyclesErrorDetail, 'Could not load cycles.')
              : undefined}
          />
        </div>

        {cyclesError ? (
          <PortalErrorBlock message={queryErrorMessage(cyclesErrorDetail, 'Could not load cycles.')} />
        ) : cockpitLoading ? (
          <PortalLoadingBlock />
        ) : cockpitError || !cockpit ? (
          <PortalErrorBlock message={queryErrorMessage(cockpitErrorDetail, 'Could not load cockpit summary.')} />
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-lg border bg-card p-4 shadow-sm">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Stores complete</p>
                <p className="mt-1 text-2xl font-semibold">
                  {cockpit.completedStores}/{cockpit.totalStores}
                </p>
                <p className="text-sm text-muted-foreground">{cockpit.completionPercent}% complete</p>
              </div>
              <div className="rounded-lg border bg-card p-4 shadow-sm">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Issues</p>
                <p className="mt-1 text-2xl font-semibold">{cockpit.issuesCount}</p>
                <p className="text-sm text-muted-foreground">Stores needing attention</p>
              </div>
              <div className="rounded-lg border bg-card p-4 shadow-sm sm:col-span-2">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Last updated</p>
                <p className="mt-1 text-sm text-foreground">
                  {dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleString() : new Date(cockpit.asOf).toLocaleString()}
                </p>
              </div>
            </div>

            {cockpit.regionBreakdown.length > 0 && (
              <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
                <table className="min-w-full text-sm">
                  <thead className="bg-muted text-left text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3">Region</th>
                      <th className="px-4 py-3">Complete</th>
                      <th className="px-4 py-3">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cockpit.regionBreakdown.map((region) => (
                      <tr key={region.region} className="border-t">
                        <td className="px-4 py-3">{region.region}</td>
                        <td className="px-4 py-3">{region.completed}</td>
                        <td className="px-4 py-3">{region.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="grid gap-4 xl:grid-cols-3">
              <div className="overflow-hidden rounded-lg border bg-card shadow-sm xl:col-span-2">
                <div className="border-b px-4 py-3">
                  <h3 className="font-medium text-foreground">Store map</h3>
                  <div className="pt-2">
                    <TrackerMapLegend />
                  </div>
                </div>
                <div className="h-[420px]">
                  {mapPoints.length === 0 ? (
                    <div className="flex h-full items-center justify-center bg-muted text-sm text-muted-foreground">
                      No store coordinates available for this cycle.
                    </div>
                  ) : (
                    <MapContainer
                      center={mapPoints[0] ?? DEFAULT_MAP_CENTER}
                      zoom={10}
                      className="h-full w-full"
                      scrollWheelZoom
                    >
                      <TileLayer
                        attribution="&copy; OpenStreetMap contributors"
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <FitBounds points={mapPoints} />
                      {cockpit.stores.filter(hasValidCoords).map((store) => {
                        const isSelected = selectedStoreId === store.storeId;
                        const trackerStatus = toTrackerStatus(store.operationalStatus);
                        const rejectedCount = store.hasRejections ? 1 : 0;
                        const pinStroke = getOperationalPinStroke(trackerStatus, rejectedCount, isSelected);
                        return (
                          <CircleMarker
                            key={store.storeId}
                            center={[store.lat!, store.lng!]}
                            radius={10}
                            fillColor={getOperationalPinColor(trackerStatus)}
                            fillOpacity={0.9}
                            color={pinStroke.color}
                            weight={pinStroke.weight}
                            eventHandlers={{ click: () => setSelectedStoreId(store.storeId) }}
                          >
                            <Popup>
                              <div className="space-y-1 min-w-[160px]">
                                <div className="font-semibold">{store.storeName}</div>
                                {store.storeNumber ? (
                                  <div className="text-xs text-gray-500">#{store.storeNumber}</div>
                                ) : null}
                                <div className="flex flex-wrap items-center gap-2">
                                  <OperationalStatusBadge status={trackerStatus} />
                                  <RejectionIndicator count={rejectedCount} />
                                </div>
                                <div className="text-xs text-gray-600">{store.completionPercent}% complete</div>
                              </div>
                            </Popup>
                          </CircleMarker>
                        );
                      })}
                    </MapContainer>
                  )}
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
                <div className="border-b px-4 py-3">
                  <h3 className="font-medium text-foreground">Stores</h3>
                </div>
                <div className="max-h-[420px] overflow-y-auto">
                  <table className="min-w-full text-sm">
                    <thead className="sticky top-0 bg-muted text-left text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2">Store</th>
                        <th className="px-3 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cockpit.stores.map((store) => (
                        <tr
                          key={store.storeId}
                          className={`border-t cursor-pointer hover:bg-muted ${selectedStoreId === store.storeId ? 'bg-primary/5' : ''}`}
                          onClick={() => setSelectedStoreId(store.storeId)}
                        >
                          <td className="px-3 py-2">
                            <div className="font-medium">{store.storeName}</div>
                            {store.storeNumber ? (
                              <div className="text-xs text-muted-foreground">#{store.storeNumber}</div>
                            ) : null}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap items-center gap-1">
                              <OperationalStatusBadge status={toTrackerStatus(store.operationalStatus)} />
                              {store.hasRejections ? <RejectionIndicator count={1} /> : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {cockpit.stores.length === 0 && (
                        <tr>
                          <td colSpan={2} className="px-3 py-6 text-center text-muted-foreground">
                            No stores assigned for this cycle.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {selectedStore && (
              <div className="rounded-lg border bg-card p-4 shadow-sm text-sm">
                <p className="font-medium">{selectedStore.storeName}</p>
                <p className="text-muted-foreground">
                  {selectedStore.completionPercent}% complete
                  {selectedStore.hasRejections ? ' · Has rejections' : ''}
                </p>
                <Link
                  href={buildPortalSectionHref('/portal', search)}
                  className="mt-2 inline-block text-primary hover:underline"
                >
                  View campaign progress →
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </PortalShell>
  );
}
